import DeleteAccountButton from '../../components/DeleteAccountButton';
import PoweredByFHO from '../../components/PoweredByFHO';
import React, { useState, useEffect, useRef } from 'react';
import ConversationActions from '../../components/ConversationActions';
import PaywallModal from '../../components/auth/PaywallModal';
import { useNavigate } from 'react-router-dom';
import API from '../../services/api_config';
import ResponseCounter from '../../components/auth/ResponseCounter';
import FeedbackButton from '../../components/FeedbackButton';
import { getPartnerTheme } from '../../config/partnerThemes';
import VoiceChoiceModal from '../../components/dashboard/modals/VoiceChoiceModal';
import MPColorsModal from '../../components/MPColorsModal';
import { prepareCoachVoice, loadCoachVoices, saveCoachVoice, previewCoachVoice, playCoachVoice, stopCoachVoice, coachVoiceActive } from '../../utils/coachVoice';

// Color schemes for the stand-alone Family Coach app. All three are light, so the dark text stays readable.
const FC_SCHEMES = {
  seafoam: { id: 'seafoam', label: 'Seafoam', note: 'Soft teal', pageBg: '#83C5BE', panelBg: '#6EB5AE', pageBgGradient: 'linear-gradient(135deg, #83C5BE 0%, #6EB5AE 100%)', cardBg: '#6EB5AE' },
  sand: { id: 'sand', label: 'Sand', note: 'Warm beige', pageBg: '#E9DCCB', panelBg: '#DCC9B0', pageBgGradient: 'linear-gradient(135deg, #E9DCCB 0%, #DCC9B0 100%)', cardBg: '#DCC9B0' },
  lavender: { id: 'lavender', label: 'Lavender', note: 'Soft violet', pageBg: '#D3CBE8', panelBg: '#C2B7DF', pageBgGradient: 'linear-gradient(135deg, #D3CBE8 0%, #C2B7DF 100%)', cardBg: '#C2B7DF' },
};
// Secondary text, dark enough to read on every background (it was faint white on teal).
const FC_MUTED = '#1f3b45';

const FamilyCoachChat = () => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [showPaywall, setShowPaywall] = useState(false);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [responsesRemaining, setResponsesRemaining] = useState(null);
  const [familyProfile, setFamilyProfile] = useState(null);
  const [children, setChildren] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [showSidebar, setShowSidebar] = useState(false);
  const [showProfileDetails, setShowProfileDetails] = useState(false);
  const [showTopicsModal, setShowTopicsModal] = useState(false);
  const [showConversationHistory, setShowConversationHistory] = useState(false);
  const [selectedCoach, setSelectedCoach] = useState('both');

  // Auto-send greeting when switching to Valor or Ama
  useEffect(() => {
    if (selectedCoach === 'both') {
      // Static welcome for Both — no API call needed
      const welcomeName = familyProfile?.preferred_name || '';
      const childNames = children && children.length > 0
        ? children.map(c => c.name).join(', ')
        : null;
      setMessages([{
        role: 'assistant',
        content: `Welcome${welcomeName ? ', ' + welcomeName : ''}! I'm your Family Coach — here to help you become better than you were as a parent. Not perfect, just better.${childNames ? '\n\nI see you have ' + childNames + ' in your family.' : ''}\n\nWhat's on your mind today? Whether it's a specific challenge, a question about development, or just needing some encouragement, I'm here to help.`,
        streaming: false
      }]);
      return;
    }
    const triggerGreeting = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      setIsLoading(true);
      try {
        const response = await fetch(`${window.location.origin}/api/v1/family/chat/stream`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ message: 'Please introduce yourself', conversation_id: null, coach_persona: selectedCoach }),
        });
        if (!response.ok) { setIsLoading(false); return; }
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let fullResponse = '';
        let newConvId = null;
        setMessages([{ role: 'assistant', content: '', streaming: true }]);
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          const chunk = decoder.decode(value);
          const lines = chunk.split('\n');
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                if (data.chunk) {
                  fullResponse += data.chunk;
                  setMessages([{ role: 'assistant', content: fullResponse, streaming: true }]);
                }
                if (data.conversation_id) newConvId = data.conversation_id;
              } catch {}
            }
          }
        }
        setMessages([{ role: 'assistant', content: fullResponse, streaming: false }]);
        if (newConvId) setConversationId(newConvId);
      } catch (e) { console.error('Greeting error:', e); }
      finally { setIsLoading(false); }
    };
    triggerGreeting();
  }, [selectedCoach]);
  const [fcConversations, setFcConversations] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [activeTopicsTab, setActiveTopicsTab] = useState('parental');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  // Stand-alone app (access code): no links to other apps, voice and color choices, credit line.
  const fcAlone = getPartnerTheme().id === 'family';
  const [fcScheme, setFcScheme] = useState(() => {
    let s = null;
    if (fcAlone) { try { s = localStorage.getItem('fcColorScheme'); } catch (e) { /* storage blocked */ } }
    return FC_SCHEMES[s] ? s : 'seafoam';
  });
  const fcColors = FC_SCHEMES[fcScheme] || FC_SCHEMES.seafoam;
  const [showColors, setShowColors] = useState(false);
  const [showVoicePanel, setShowVoicePanel] = useState(false);
  const [voiceList, setVoiceList] = useState([]);
  const [voiceChoice, setVoiceChoice] = useState(null);
  // PLATFORM_VOICE_V1: the main Platform gets the voice choice too
  useEffect(() => {
    if (fcAlone) return;
    prepareCoachVoice();
    loadCoachVoices().then((d) => { if (d) { setVoiceList(d.voices); setVoiceChoice(d.choice); } });
  }, []);
  useEffect(() => {
    if (!fcAlone) return;
    prepareCoachVoice();
    loadCoachVoices().then((d) => { if (d) { setVoiceList(d.voices); setVoiceChoice(d.choice); } });
    API.get('/api/v1/family/color-scheme').then((r) => {
      const id = r.data && r.data.active;
      if (id && FC_SCHEMES[id]) {
        try { localStorage.setItem('fcColorScheme', id); } catch (e) { /* storage blocked */ }
        setFcScheme(id);
      }
    }).catch(() => {});
  }, []);
  const chooseScheme = (id) => {
    if (!FC_SCHEMES[id]) return;
    try { localStorage.setItem('fcColorScheme', id); } catch (e) { /* storage blocked */ }
    setFcScheme(id);
    API.post('/api/v1/family/color-scheme', { scheme: id }).catch(() => {});
  };

  // ============ 50 Parental Topics ============
  const parentalTopics = [
    // Behavior & Discipline (5)
    "Managing Tantrums & Meltdowns",
    "Effective Discipline Strategies",
    "Dealing with Defiance",
    "Setting Healthy Boundaries",
    "Teaching Respect & Manners",
    
    // Emotional Development (5)
    "Building Your Child's Confidence",
    "Helping with Anxiety & Worry",
    "Managing Big Emotions",
    "Dealing with Anger Issues",
    "Building Emotional Intelligence",
    
    // Social Skills (4)
    "Making & Keeping Friends",
    "Handling Sibling Rivalry",
    "Dealing with Bullying",
    "Teaching Sharing & Empathy",
    
    // Screen Time & Technology (3)
    "Managing Screen Time",
    "Social Media & Kids",
    "Online Safety & Privacy",
    
    // School & Learning (3)
    "Homework Struggles",
    "Motivating Reluctant Learners",
    "School Anxiety & Stress",
    
    // Health & Wellness (3)
    "Sleep Problems & Bedtime",
    "Building Healthy Habits",
    "Mental Health Awareness",
    
    // Communication (3)
    "Getting Kids to Open Up",
    "Talking About Hard Topics",
    "Building Trust & Honesty",
    
    // Life Skills (4)
    "Age-Appropriate Chores",
    "Teaching Responsibility",
    "Independence & Self-Reliance",
    "Decision Making Skills",
    
    // FHO Framework Topics (25 - 50%)
    "Being a Fholific Leader for Your Family",
    "Teaching Children the 12 Life Choices",
    "Helping Kids Avoid the 3D Swamp",
    "Building Your Child's I-WE Relationships",
    "Teaching Freedom vs Dependency",
    "Cultivating Clarity in Family Communication",
    "Modeling Power, Flow & Clarity",
    "Protecting Kids from Hood of the Victim",
    "Family Oneness vs Discord",
    "Raising Free Happy Ones",
    "Teaching Beliefs, Feelings & Actions",
    "Building Your Child's MindStreams",
    "Escaping So-Soville as a Family",
    "Using Autocebos in Parenting",
    "The UED Method for Family Growth",
    "Teaching Children About Their I-AM",
    "Nurturing Your Child's I-THEE Connection",
    "Building Strong I-ME in Children",
    "Family Antigravity Relay Activation",
    "Creating a Culture of Freedom & Happiness",
    "Choosing Happiness Over Despair as a Family",
    "Building Power Through Family Beliefs",
    "Flow State in Family Relationships",
    "The Trust-Feedback Loop in Parenting",
    "36 Dimensions of Self for Children"
  ];

  // ============ 30 Grandparent Topics ============
  const grandparentTopics = [
    // Building Connections (4)
    "Creating Lasting Memories Together",
    "Building Strong Bonds from a Distance",
    "Using Technology to Stay Connected",
    "Making Visits Memorable",
    
    // Respecting Boundaries (4)
    "Respecting Parents' Boundaries",
    "Supporting Without Overstepping",
    "Navigating Different Parenting Styles",
    "Balancing Involvement Levels",
    
    // Sharing Heritage & Wisdom (4)
    "Passing Down Family Stories",
    "Teaching Family Traditions",
    "Preserving Family History",
    "Life Lessons to Share",
    
    // The Six Grandparent Roles (4)
    "Being a Storyteller & Historian",
    "Being a Teacher & Mentor",
    "Being a Safe Haven",
    "Being a Bridge Between Generations",
    
    // Activities & Communication (4)
    "Outdoor Adventures & Nature",
    "Reading & Storytelling Time",
    "Open Communication with Adult Children",
    "Encouraging Their Interests & Dreams",
    
    // FHO Framework Topics (15 - 50%)
    "Being a Fholific Grandparent Leader",
    "Passing Down the 12 Life Choices",
    "Teaching Grandchildren About Freedom",
    "Sharing Wisdom on Happiness & Oneness",
    "Modeling Power, Flow & Clarity",
    "Helping Grandkids Avoid the 3D Swamp",
    "Legacy of Freedom vs Dependency",
    "Building I-WE Across Generations",
    "Teaching the Hood of the Victim Concept",
    "Raising the Next Generation of Free Happy Ones",
    "Autocebos for Grandparent Wisdom",
    "The UED Method Across Generations",
    "Building Your Grandchild's MindStreams",
    "Choosing Oneness Over Discord",
    "Creating Generational Freedom & Happiness"
  ];

  // Check if user is a grandparent
  const isGrandparent = familyProfile?.roles?.includes('grandparent');

  // Render markdown links in messages
  const renderMessageContent = (text) => {
    if (!text) return text;
    // Stand-alone app: a link the coach still writes shows as its words only.
    if (fcAlone) return String(text).replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
    // Match markdown links: [text](url)
    const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    
    while ((match = linkRegex.exec(text)) !== null) {
      // Add text before the link
      if (match.index > lastIndex) {
        parts.push(text.slice(lastIndex, match.index));
      }
      // Add the link as a clickable element
      const linkText = match[1];
      const linkUrl = match[2];
      parts.push(
        <a 
          key={match.index}
          href={linkUrl}
          style={{ 
            color: '#264653',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px', 
            textDecoration: 'underline',
            cursor: 'pointer'
          }}
          onClick={(e) => {
            e.preventDefault();
            window.location.href = linkUrl;
          }}
        >
          {linkText}
        </a>
      );
      lastIndex = match.index + match[0].length;
    }
    
    // Add remaining text
    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }
    
    return parts.length > 0 ? parts : text;
  };

  // ============ Voice Hook ============
  const useVoiceChat = () => {
    const [isListening, setIsListening] = useState(false);
    const [transcript, setTranscript] = useState('');
    const [voiceEnabled, setVoiceEnabled] = useState(() => {
      const saved = localStorage.getItem('familyCoachVoiceEnabled');
      return saved ? JSON.parse(saved) : false;
    });
    const [isSpeaking, setIsSpeaking] = useState(false);
    
    const recognitionRef = useRef(null);
    const synthRef = useRef(window.speechSynthesis);
    
    const isSupported = typeof window !== 'undefined' && 
      ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window);
    
    useEffect(() => {
      if (!isSupported) return;
      
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'en-US';
      
      recognitionRef.current.onresult = (event) => {
        let finalTranscript = '';
        let interimTranscript = '';
        
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript;
          } else {
            interimTranscript += transcript;
          }
        }
        
        setTranscript(finalTranscript || interimTranscript);
      };
      
      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
      
      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
      };
      
      return () => {
        if (recognitionRef.current) {
          recognitionRef.current.abort();
        }
      };
    }, [isSupported]);
    
    const startListening = () => {
      if (!isSupported || !recognitionRef.current) return;
      
      setTranscript('');
      setIsListening(true);
      
      try {
        recognitionRef.current.start();
      } catch (e) {
        console.error('Error starting recognition:', e);
      }
    };
    
    const stopListening = () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      setIsListening(false);
    };
    
    // Strip markdown for voice - removes links, bold, italic, underline
    const stripMarkdownForVoice = (text) => {
      return text
        .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')  // [link text](url) → link text
        .replace(/\*\*([^*]+)\*\*/g, '$1')        // **bold** → bold
        .replace(/\*([^*]+)\*/g, '$1')            // *italic* → italic
        .replace(/__([^_]+)__/g, '$1')            // __underline__ → underline
        .replace(/_([^_]+)_/g, '$1')              // _italic_ → italic
        .replace(/FHO-OS/gi, 'foe OS')            // FHO-OS → foe OS
        .replace(/FHO/gi, 'foe');                 // FHO → foe
    };

    const speak = (text) => {
      if (!voiceEnabled) return;
      if (coachVoiceActive()) {
        if (synthRef.current) synthRef.current.cancel();
        playCoachVoice(stripMarkdownForVoice(text), () => setIsSpeaking(true), () => setIsSpeaking(false), (rest) => speakDevice(rest));
        return;
      }
      speakDevice(text);
    };
    const speakDevice = (text) => {
      if (!synthRef.current || !voiceEnabled) return;
      
      synthRef.current.cancel();
      
      // Clean markdown links before speaking
      const cleanText = stripMarkdownForVoice(text);
      const utterance = new SpeechSynthesisUtterance(cleanText);
      utterance.rate = 0.90;
      utterance.pitch = 0.85;
      utterance.volume = 1.0;
      
      const voices = synthRef.current.getVoices();
      const femaleVoice = voices.find(v => 
        v.name.includes('Samantha') || 
        v.name.includes('Female') ||
        v.name.includes('Karen') ||
        v.name.includes('Victoria')
      );
      if (femaleVoice) {
        utterance.voice = femaleVoice;
      }
      
      utterance.onstart = () => setIsSpeaking(true);
      utterance.onend = () => setIsSpeaking(false);
      utterance.onerror = () => setIsSpeaking(false);
      
      synthRef.current.speak(utterance);
    };
    
    const stopSpeaking = () => {
      stopCoachVoice();
      if (synthRef.current) {
        synthRef.current.cancel();
        setIsSpeaking(false);
      }
    };
    
    const setVoiceOn = (on) => {
      setVoiceEnabled(on);
      localStorage.setItem('familyCoachVoiceEnabled', JSON.stringify(on));
      if (!on) stopSpeaking();
    };

    const toggleVoice = () => {
      const newValue = !voiceEnabled;
      setVoiceEnabled(newValue);
      localStorage.setItem('familyCoachVoiceEnabled', JSON.stringify(newValue));
      if (!newValue) {
        stopSpeaking();
      }
    };
    
    return {
      isListening,
      transcript,
      voiceEnabled,
      isSpeaking,
      isSupported,
      startListening,
      stopListening,
      speak,
      stopSpeaking,
      toggleVoice,
      setVoiceOn
    };
  };

  const voice = useVoiceChat();

  // Update input when voice transcript changes
  useEffect(() => {
    if (voice.transcript) {
      setInput(voice.transcript);
    }
  }, [voice.transcript]);
  // Auto-send when browser silence detection cuts mic
  const wasListeningRef = useRef(false);
  useEffect(() => {
    if (wasListeningRef.current && !voice.isListening && input.trim()) {
      setTimeout(() => sendMessage(), 150);
    }
    wasListeningRef.current = voice.isListening;
  }, [voice.isListening]);


  // Check for pending cross-app prompt after profile loads
  useEffect(() => {
    if (familyProfile) {
      const pendingPrompt = localStorage.getItem('familyCoach_pendingPrompt');
      if (pendingPrompt) {
        console.log('📬 Found pending prompt for Family Coach:', pendingPrompt);
        localStorage.removeItem('familyCoach_pendingPrompt');
        setInput(pendingPrompt);
        setTimeout(() => {
          sendMessage(pendingPrompt);
        }, 800);
      }
    }
  }, [familyProfile]);

  // Cross-App Integration: Handle URL params for deep linking from other FHO apps
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const promptParam = urlParams.get('prompt');
    const autoSend = urlParams.get('send') === 'true';
    
    if (promptParam) {
      const decodedPrompt = decodeURIComponent(promptParam);
      
      // Store in localStorage to survive profile setup redirect
      if (autoSend) {
        localStorage.setItem('familyCoach_pendingPrompt', decodedPrompt);
        console.log('📝 Stored pending prompt for Family Coach:', decodedPrompt);
      }
      
      setInput(decodedPrompt);
      
      // Clear URL params after reading (clean URL)
      window.history.replaceState({}, '', window.location.pathname);
      
      // Auto-send if requested (wait for profile to load)
      if (autoSend) {
        setTimeout(() => {
          sendMessage(decodedPrompt);
        }, 1000);
      }
    }
  }, [familyProfile]);

  // Journey Focus: Auto-pickup from Journey Dashboard Recommended Focus
  useEffect(() => {
    const journeyPrompt = localStorage.getItem('journeyFocusPrompt');
    if (journeyPrompt && familyProfile) {
      localStorage.removeItem('journeyFocusPrompt');
      localStorage.removeItem('journeyFocusCategory');
      localStorage.removeItem('guidedSessionId');
      localStorage.removeItem('guidedSessionDimension');
      localStorage.removeItem('guidedSessionFocus');
      localStorage.removeItem('guidedSessionFollowUps');
      
      setInput(journeyPrompt);
      setTimeout(() => {
        sendMessage(journeyPrompt);
      }, 800);
    }
  }, [familyProfile]);

  useEffect(() => {
    loadFamilyData();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadFamilyData = async () => {
    try {
      const [profileRes, childrenRes] = await Promise.all([
        API.get('/api/v1/family/profile'),
        API.get('/api/v1/family/children')
      ]);
      
      setFamilyProfile(profileRes.data);
      setChildren(childrenRes.data || []);
      
      const welcomeName = profileRes.data?.preferred_name || 'there';
      const childNames = (childrenRes.data || []).map(c => c.name).join(', ');
      
      setMessages([{
        role: 'assistant',
        content: `Welcome${welcomeName ? `, ${welcomeName}` : ''}! I'm your Family Coach — here to help you become better than you were as a parent. Not perfect, just better.${childNames ? `\n\nI see you have ${childNames} in your family.` : ''}\n\nWhat's on your mind today? Whether it's a specific challenge, a question about development, or just needing some encouragement, I'm here to help.`
      }]);
    } catch (err) {
      setMessages([{
        role: 'assistant',
        content: `Welcome to Family Coach! I'm here to help you become better than you were as a parent. Not perfect, just better.\n\nTo give you the best support, I'd love to learn about your family. Would you like to set up your family profile now?`
      }]);
    }
  };

  // Load conversation history
  const loadFCHistory = async () => {
    setLoadingHistory(true);
    try {
      const res = await API.get("/api/v1/family/conversations");
      setFcConversations(res.data.conversations || []);
    } catch (error) {
      console.error("Error loading history:", error);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Load specific conversation messages
  const loadConversationMessages = async (convId) => {
    try {
      const res = await API.get(`/api/v1/family/conversations/${convId}`);
      const loadedMessages = res.data.messages.map(msg => ({
        role: msg.role,
        content: msg.content
      }));
      setMessages(loadedMessages);
      setConversationId(convId);
      setShowConversationHistory(false);
    } catch (error) {
      console.error("Error loading conversation:", error);
    }
  };

  const sendMessage = async (directPrompt = null) => {
    const messageToSend = (typeof directPrompt === 'string') ? directPrompt : input.trim();
    if (!messageToSend || isLoading) return;
    
    const userMessage = messageToSend;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);
    
    // Close topics modal if open
    setShowTopicsModal(false);
    
    // Add empty assistant message placeholder for streaming
    setMessages(prev => [...prev, { 
      role: 'assistant', 
      content: '',
      isStreaming: true
    }]);
    
    try {
      const token = localStorage.getItem('token');
      const apiUrl = window.location.origin;
      
      const response = await fetch(`${window.location.origin}/api/v1/family/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          message: userMessage,
          conversation_id: conversationId,
          coach_persona: selectedCoach,
        })
      });

      // Check for free tier exhausted FIRST
      if (response.status === 403) {
        setShowPaywall(true);
        setMessages(prev => prev.slice(0, -1)); // Remove the empty streaming message
        setIsLoading(false);
        return;
      }
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let accumulatedText = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));

              if (data.error) {
                throw new Error(data.error);
              }

              if (data.responses_remaining !== undefined) {
                setResponsesRemaining(data.responses_remaining);
                // Show paywall if exhausted (same as Emit)
                if (data.responses_remaining === 0) {
                  setTimeout(() => setShowPaywall(true), 2000);
                }
              }
              if (data.chunk) {
                accumulatedText += data.chunk;

                setMessages(prev => {
                  const updated = [...prev];
                  updated[updated.length - 1] = {
                    role: 'assistant',
                    content: accumulatedText,
                    isStreaming: !data.done
                  };
                  return updated;
                });
              }

              if (data.done) {
                setMessages(prev => {
                  const updated = [...prev];
                  updated[updated.length - 1] = {
                    role: 'assistant',
                    content: accumulatedText,
                    isStreaming: false
                  };
                  return updated;
                });

                if (voice.voiceEnabled && accumulatedText) {
                  setTimeout(() => {
                    voice.speak(accumulatedText);
                  }, 300);
                }
              }
            } catch (parseError) {
              console.error('Error parsing SSE data:', parseError);
            }
          }
        }
      }
    } catch (err) {
      console.error('Streaming error:', err);
      // Check if error is due to missing profile (400 error)
      if (err.message && err.message.includes('400')) {
        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = {
            role: 'assistant',
            content: "It looks like you haven't set up your family profile yet. Let me take you there now...",
            isStreaming: false
          };
          return updated;
        });
        // Redirect to setup after brief delay
        setTimeout(() => navigate('/family-coach/setup'), 1500);
        return;
      }
      setMessages(prev => {
        const updated = [...prev];
        updated[updated.length - 1] = {
          role: 'assistant',
          content: "I apologize, but I'm having trouble responding right now. Please try again in a moment.",
          isStreaming: false
        };
        return updated;
      });
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const getAgeLabel = (ageValue) => {
    const ageLabels = {
      '0-3mo': '0-3 months', '3-6mo': '3-6 months', '6-9mo': '6-9 months', '9-12mo': '9-12 months',
      '12-18mo': '12-18 months', '18-24mo': '18-24 months', '2': '2 years', '3': '3 years',
      '4': '4 years', '5': '5 years', '6': '6 years', '7': '7 years', '8': '8 years',
      '9': '9 years', '10': '10 years', '11': '11 years', '12': '12 years', '13': '13 years',
      '14': '14 years', '15': '15 years', '16': '16 years', '17': '17 years', '18+': '18+ years'
    };
    return ageLabels[ageValue] || ageValue;
  };

  return (
    <div style={{
      minHeight: '100vh',
      height: '100vh',
      background: fcColors.pageBg,
      display: 'flex',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      overflow: 'hidden'
    }}>
      {/* Sidebar Backdrop - click to close */}
      {showSidebar && (
        <div 
          onClick={() => setShowSidebar(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 998
          }}
        />
      )}
      
      {/* Sidebar - Fixed position overlay on mobile */}
      <div style={{
        position: 'fixed',
        top: 0,
        right: showSidebar ? 0 : '-280px',
        width: '280px',
        height: '100%',
        background: fcColors.panelBg,
        transition: 'right 0.3s ease',
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 999
      }}>
        <div style={{ padding: '20px', minWidth: '280px' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '24px'
          }}>
            <div style={{
              width: '48px',
              height: '48px',
              background: 'linear-gradient(135deg, #2A9D8F, #52B4A8)',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '24px'
            }}>{selectedCoach === 'valor' ? '👨' : selectedCoach === 'ama' ? '👩' : '👨‍👩‍👧'}</div>
            <div>
              <div style={{ color: '#264653', fontWeight: '700', fontSize: '18px' }}>
                {selectedCoach === 'valor' ? 'Valor' : selectedCoach === 'ama' ? 'Ama' : 'Family Coach'}
              </div>
              <div style={{ color: FC_MUTED, fontSize: '12px', fontStyle: 'italic' }}>
                {selectedCoach === 'valor' ? "\"A Father\'s Wisdom, A Father\'s Love\"" : selectedCoach === 'ama' ? "\"A Mother\'s Wisdom, A Mother\'s Love\"" : null}
              </div>
            </div>
          </div>
          
          {/* Coach Selector */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
            {[
              { key: 'both', label: '👨‍👩‍👧 Both', sub: 'Family Coach' },
              { key: 'valor', label: '👨 Valor', sub: 'Father' },
              { key: 'ama', label: '👩 Ama', sub: 'Mother' },
            ].map(opt => (
              <button
                key={opt.key}
                onClick={() => { setSelectedCoach(opt.key); setMessages([]); setConversationId(null); setShowSidebar(false); }}
                style={{
                  flex: 1, padding: '8px 4px',
                  background: selectedCoach === opt.key ? 'rgba(42,157,143,0.3)' : 'rgba(255,255,255,0.05)',
                  border: selectedCoach === opt.key ? '1px solid #2A9D8F' : '1px solid rgba(255,255,255,0.15)',
                  borderRadius: '8px', cursor: 'pointer', textAlign: 'center',
                  transition: 'all 0.2s',
                }}
              >
                <div style={{ fontSize: '14px' }}>{opt.label}</div>
                <div style={{ fontSize: '12px', color: FC_MUTED, marginTop: '2px' }}>{opt.sub}</div>
              </button>
            ))}
          </div>

          {/* Family Info - Collapsible */}
          <div style={{ marginBottom: '20px' }}>
            {familyProfile ? (
              <>
                <button
                  onClick={() => setShowProfileDetails(!showProfileDetails)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.9)',
                    borderRadius: '6px',
                    color: '#264653',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: showProfileDetails ? '12px' : '0'
                  }}
                >
                  <span style={{ fontWeight: '600' }}>👨‍👩‍👧 View Family Profile</span>
                  <span style={{ 
                    transform: showProfileDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease'
                  }}>▼</span>
                </button>
                
                {showProfileDetails && (
                  <div style={{
                    padding: '12px',
                    background: 'rgba(255,255,255,0.03)',
                    borderRadius: '6px',
                    border: '1px solid rgba(255,255,255,0.05)'
                  }}>
                    <div style={{
                      color: FC_MUTED,
                      fontSize: '12px',
                      marginBottom: '8px',
                      textTransform: 'uppercase',
                      letterSpacing: '1px'
                    }}>Your Family</div>
                    
                    <div style={{
                      padding: '8px',
                      background: 'rgba(255,255,255,0.05)',
                      borderRadius: '6px',
                      marginBottom: '12px'
                    }}>
                      <div style={{ color: '#264653', fontWeight: '600', marginBottom: '2px' }}>
                        {familyProfile.preferred_name || 'Parent'}
                      </div>
                      <div style={{ color: FC_MUTED, fontSize: '13px' }}>
                        {familyProfile.family_structure?.replace('_', ' ')}
                      </div>
                    </div>
                    
                    <div style={{
                      color: FC_MUTED,
                      fontSize: '12px',
                      marginBottom: '8px',
                      textTransform: 'uppercase',
                      letterSpacing: '1px'
                    }}>Children</div>
                    
                    <div>
                      {children.length > 0 ? children.map((child, idx) => (
                        <div key={idx} style={{
                          padding: '8px 10px',
                          background: 'rgba(255,255,255,0.05)',
                          borderRadius: '6px',
                          marginBottom: '6px'
                        }}>
                          <div style={{ color: '#264653', fontWeight: '600', fontSize: '14px' }}>{child.name}</div>
                          <div style={{ color: FC_MUTED, fontSize: '12px' }}>
                            {getAgeLabel(child.age)} • {child.relationship}
                          </div>
                        </div>
                      )) : (
                        <div style={{ color: FC_MUTED, fontSize: '13px' }}>No children added</div>
                      )}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <button
                onClick={() => navigate('/family-coach/setup')}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: 'linear-gradient(135deg, #A8D4F0, #C5E4F7)',
                  border: 'none',
                  borderRadius: '6px',
                  color: '#264653',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >Set Up Family Profile</button>
            )}
          </div>
          
          {/* Sidebar Buttons */}
          <div style={{ paddingTop: '20px', borderTop: '1px solid rgba(255,255,255,0.9)' }}>
            {/* Topics Button */}
            {familyProfile && (
              <button
                onClick={() => navigate('/family-coach/setup')}
                style={{
                  width: '100%',
                  padding: '8px',
                  background: 'rgba(74,144,226,0.2)',
                  border: '1px solid rgba(74,144,226,0.5)',
                  borderRadius: '6px',
                  color: '#264653',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: 'pointer',
                  marginBottom: '6px'
                }}
              >✏️ Edit Profile</button>
            )}
            
            <button
              onClick={() => setShowTopicsModal(true)}
              style={{
                width: '100%',
                padding: '8px',
                background: 'linear-gradient(135deg, #9b59b6, #8e44ad)',
                border: 'none',
                borderRadius: '6px',
                color: '#264653',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                marginBottom: '6px',
                fontWeight: '600'
              }}
            >{isGrandparent ? '💡💝' : '💡'} Explore Topics</button>
            
            <button
              onClick={() => { setShowSidebar(false); loadFCHistory(); setShowConversationHistory(true); }}
              style={{
                width: '100%',
                padding: '8px',
                background: 'transparent',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: '6px',
                color: FC_MUTED,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                marginBottom: '6px'
              }}
            >📜 Conversation History</button>
            <button
              onClick={() => { setShowSidebar(false); setConversationId(null); setMessages([]); loadFamilyData(); }}
              style={{
                width: '100%',
                padding: '8px',
                background: 'transparent',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: '6px',
                color: FC_MUTED,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                marginBottom: '6px'
              }}
            >🔄 New Conversation</button>
            {fcAlone && (
              <>
                <button onClick={() => { setShowSidebar(false); setShowVoicePanel(true); }} style={{
                  width: '100%', padding: '8px', background: 'transparent', border: '1px solid rgba(38,70,83,0.35)',
                  borderRadius: '6px', color: '#264653', cursor: 'pointer', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', gap: '8px', marginBottom: '6px', fontSize: '15px'
                }}>{'\uD83D\uDD0A'} Voice</button>
                <button onClick={() => { setShowSidebar(false); setShowColors(true); }} style={{
                  width: '100%', padding: '8px', background: 'transparent', border: '1px solid rgba(38,70,83,0.35)',
                  borderRadius: '6px', color: '#264653', cursor: 'pointer', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', gap: '8px', marginBottom: '6px', fontSize: '15px'
                }}>{'\uD83C\uDFA8'} Colors</button>
              </>
            )}

            {/* ── Change Home App ── */}
            {!fcAlone && (
              <button onClick={() => { setShowSidebar(false); setShowVoicePanel(true); }} style={{
                width: '100%', padding: '8px', background: 'transparent', border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: '6px', color: FC_MUTED, cursor: 'pointer', display: 'flex', alignItems: 'center',
                justifyContent: 'center', gap: '8px', marginBottom: '6px', fontSize: '15px'
              }}>{'\uD83D\uDD0A'} Voice</button>
            )}
            {!fcAlone && (<>
            <button
              onClick={() => { setShowSidebar(false); navigate('/select-home-app'); }}
              style={{
                width: '100%',
                padding: '8px',
                background: 'rgba(79,70,229,0.15)',
                border: '1px solid rgba(79,70,229,0.4)',
                borderRadius: '6px',
                color: '#a5b4fc',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                marginBottom: '6px',
                fontWeight: '600'
              }}
            >🏠 Change Home App</button>
            
            <button
              onClick={() => navigate('/emit-the-bible')}
              style={{
                width: '100%',
                padding: '8px',
                background: 'transparent',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: '6px',
                color: FC_MUTED,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                marginBottom: '6px'
              }}
            >✨ Emit / 💫 SAGE</button>
            
            <button
              onClick={() => navigate('/mental-performance')}
              style={{
                width: '100%',
                padding: '8px',
                background: 'transparent',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: '6px',
                color: FC_MUTED,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                marginBottom: '6px'
              }}
            >🏆 Mental Performance</button>
            
            <button
              onClick={() => window.location.href = '/safe'}
              style={{
                padding: '12px',
                background: 'rgba(255,255,255,0.9)',
                border: 'none',
                borderRadius: '6px',
                color: '#264653',
                textAlign: 'left',
                cursor: 'pointer',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                marginBottom: '6px'
              }}
            >🛡️ SAFE Buddy</button>
            
            <button
              onClick={() => window.location.href = '/journey'}
              style={{
                width: '100%',
                padding: '8px',
                background: 'linear-gradient(135deg, rgba(102,126,234,0.3), rgba(118,75,162,0.3))',
                border: '1px solid rgba(255,215,0,0.3)',
                borderRadius: '6px',
                color: '#FFD700',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                marginBottom: '6px'
              }}
            >✨ My Journey</button>
            
            </>)}
            <button
              onClick={() => {
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                window.location.href = '/login';
              }}
              style={{
                width: '100%',
                padding: '8px',
                background: 'rgba(220,53,69,0.1)',
                border: '1px solid rgba(220,53,69,0.3)',
                borderRadius: '6px',
                color: '#dc3545',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >🚪 Logout</button>
            <DeleteAccountButton variant="light" />
            {fcAlone && (<div style={{ padding: '14px 4px 4px', fontSize: '14px', color: FC_MUTED, textAlign: 'center' }}>Built by MindStreams Technology</div>)}
          </div>
        </div>
      </div>

      {/* Main Chat Area */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        
        {/* Header */}
        <div style={{
          padding: '16px 20px',
          borderBottom: '1px solid rgba(255,255,255,0.9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              background: 'linear-gradient(135deg, #2A9D8F, #52B4A8)',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '20px'
            }}>👨‍👩‍👧</div>
            <div>
              <div style={{ color: '#264653', fontWeight: '600', fontSize: '18px' }}>
                {selectedCoach === 'valor' ? 'Valor' : selectedCoach === 'ama' ? 'Ama' : 'Family Coach'}
              </div>
              <div style={{ color: FC_MUTED, fontSize: '13px', fontStyle: 'italic' }}>
                {selectedCoach === 'valor' ? 'A Father’s Wisdom' : selectedCoach === 'ama' ? 'A Mother’s Wisdom' : null}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <PoweredByFHO light />
            <button
            onClick={() => setShowSidebar(!showSidebar)}
            style={{
              background: 'none',
              border: 'none',
              color: '#264653',
              fontSize: '24px',
              cursor: 'pointer',
              padding: '4px'
            }}
          >☰</button>
          </div>
        </div>

        {/* Free Trial Counter */}
        {responsesRemaining !== null && (
          <div style={{ position: 'fixed', top: '80px', right: '20px', zIndex: 1000 }}>
            <ResponseCounter responsesRemaining={responsesRemaining} />
          </div>
        )}
        {/* Messages */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '20px',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px'
        }}>
          {messages.map((msg, idx) => (
            <div
              key={idx}
              style={{
                display: 'flex',
                justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start'
              }}
            >
              <div style={{
                maxWidth: '75%',
                padding: '14px 18px',
                borderRadius: msg.role === 'user' ? '20px 20px 4px 20px' : '20px 20px 20px 4px',
                background: msg.role === 'user' 
                  ? 'linear-gradient(135deg, #A8D4F0, #C5E4F7)'
                  : 'rgba(255,255,255,0.9)',
                color: '#264653',
                whiteSpace: 'pre-wrap',
                lineHeight: '1.5'
              }}>
                {renderMessageContent(msg.content) || (msg.isStreaming ? '' : '')}
                {msg.isStreaming && (
                  <span style={{ 
                    display: 'inline-block',
                    width: '8px',
                    height: '16px',
                    background: 'rgba(255,255,255,0.7)',
                    marginLeft: '2px',
                    animation: 'blink 1s infinite'
                  }}></span>
                )}
                {msg.role === 'assistant' && !msg.isStreaming && idx > 0 && idx === messages.length - 1 && (
                  <FeedbackButton sessionId={`fc_${Date.now()}`} messageIndex={idx} />
                )}
              </div>
            </div>
          ))}
          
          {isLoading && messages.length > 0 && !messages[messages.length - 1]?.isStreaming && (
            <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
              <div style={{
                padding: '14px 18px',
                borderRadius: '20px 20px 20px 4px',
                background: 'rgba(255,255,255,0.9)',
                color: FC_MUTED
              }}>
                <span style={{ animation: 'pulse 1.5s infinite' }}>Thinking...</span>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div style={{
          padding: '12px 16px',
          paddingBottom: 'max(12px, env(safe-area-inset-bottom))',
          borderTop: '1px solid rgba(255,255,255,0.9)',
          background: fcColors.pageBg
        }}>
          <div style={{ marginBottom: '12px' }}>
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask about parenting..."
              rows={1}
              style={{
                width: '100%',
                padding: '14px 16px',
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: '16px',
                color: '#264653',
                fontSize: '16px',
                resize: 'none',
                outline: 'none',
                minHeight: '48px',
                maxHeight: '120px',
                boxSizing: 'border-box'
              }}
            />
          </div>
          
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: '12px'
          }}>
            {voice.isSupported && (
              <button
                type="button"
                onClick={() => {
                  if (voice.isListening) {
                    voice.stopListening();
                    if (voice.transcript.trim()) {
                      setTimeout(() => sendMessage(), 100);
                    }
                  } else {
                    voice.startListening();
                  }
                }}
                disabled={isLoading}
                style={{
                  padding: '12px',
                  background: voice.isListening
                    ? 'linear-gradient(135deg, #f43f5e 0%, #e11d48 100%)'
                    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                  borderRadius: '50%',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  fontSize: '20px',
                  width: '48px',
                  height: '48px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: isLoading ? 0.5 : 1,
                  boxShadow: voice.isListening ? '0 0 0 3px rgba(244,63,94,0.3)' : 'none',
                  transition: 'all 0.3s'
                }}
                title={voice.isListening ? 'Click to stop and send' : 'Click to speak'}
              >
                {voice.isListening ? '⏹️' : '🎤'}
              </button>
            )}

            {voice.isSupported && (
              <button
                type="button"
                onClick={() => voice.toggleVoice()}
                disabled={isLoading}
                style={{
                  padding: '12px',
                  background: voice.voiceEnabled
                    ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                    : 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
                  border: 'none',
                  borderRadius: '50%',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  fontSize: '20px',
                  width: '48px',
                  height: '48px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: isLoading ? 0.5 : 1,
                  transition: 'all 0.3s'
                }}
                title={voice.voiceEnabled ? 'Disable voice responses' : 'Enable voice responses'}
              >
                {voice.voiceEnabled ? '🔊' : '🔇'}
              </button>
            )}

            <button
              onClick={sendMessage}
              disabled={!input.trim() || isLoading}
              style={{
                minWidth: '80px',
                height: '48px',
                padding: '0 16px',
                borderRadius: '24px',
                background: input.trim() ? 'linear-gradient(135deg, #A8D4F0, #C5E4F7)' : 'rgba(255,255,255,0.9)',
                border: 'none',
                color: '#264653',
                fontSize: '15px',
                fontWeight: '500',
                cursor: input.trim() ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                whiteSpace: 'nowrap',
                gap: '4px'
              }}
            >Send →</button>
          </div>
          
          {voice.isListening && (
            <div style={{
              marginTop: '8px',
              textAlign: 'center',
              color: '#f43f5e',
              fontSize: '13px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px'
            }}>
              <span style={{ 
                width: '8px', 
                height: '8px', 
                background: '#f43f5e', 
                borderRadius: '50%',
                animation: 'pulse 1s infinite'
              }}></span>
              Listening...
            </div>
          )}
          
          {voice.isSpeaking && (
            <div style={{
              marginTop: '8px',
              textAlign: 'center',
              color: '#10b981',
              fontSize: '13px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px'
            }}>
              <span style={{ 
                width: '8px', 
                height: '8px', 
                background: '#10b981', 
                borderRadius: '50%',
                animation: 'pulse 1s infinite'
              }}></span>
              Speaking...
              <button
                onClick={() => voice.stopSpeaking()}
                style={{
                  marginLeft: '8px',
                  padding: '4px 8px',
                  background: 'rgba(16,185,129,0.2)',
                  border: '1px solid rgba(16,185,129,0.3)',
                  borderRadius: '4px',
                  color: '#10b981',
                  fontSize: '12px',
                  cursor: 'pointer'
                }}
              >Stop</button>
            </div>
          )}
        </div>
      </div>

      {/* Topics Modal */}
      {showTopicsModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: fcColors.panelBg,
            borderRadius: '16px',
            maxWidth: '900px',
            width: '100%',
            maxHeight: '80vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Modal Header */}
            <div style={{
              padding: '20px 24px',
              borderBottom: '1px solid rgba(255,255,255,0.9)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '24px' }}>{activeTopicsTab === 'parental' ? '💡' : '💝'}</span>
                <div>
                  <div style={{ color: '#264653', fontSize: '20px', fontWeight: '700' }}>
                    {activeTopicsTab === 'parental' ? 'Explore 50 Parental Topics' : 'Explore 40 Grandparent Topics'}
                  </div>
                  <div style={{ color: FC_MUTED, fontSize: '14px' }}>
                    Select a topic to explore with Family Coach
                  </div>
                </div>
              </div>
              <button
                onClick={() => setShowTopicsModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: FC_MUTED,
                  fontSize: '28px',
                  cursor: 'pointer',
                  padding: '4px'
                }}
              >×</button>
            </div>
            
            {/* Tabs - Only show if user is a grandparent */}
            {isGrandparent && (
              <div style={{
                display: 'flex',
                borderBottom: '1px solid rgba(255,255,255,0.9)',
                padding: '0 24px'
              }}>
                <button
                  onClick={() => setActiveTopicsTab('parental')}
                  style={{
                    padding: '12px 20px',
                    background: 'none',
                    border: 'none',
                    color: activeTopicsTab === 'parental' ? 'white' : 'rgba(255,255,255,0.5)',
                    fontSize: '15px',
                    fontWeight: activeTopicsTab === 'parental' ? '600' : '400',
                    cursor: 'pointer',
                    borderBottom: activeTopicsTab === 'parental' ? '2px solid #667eea' : '2px solid transparent',
                    marginBottom: '-1px'
                  }}
                >
                  💡 Parental Topics
                </button>
                <button
                  onClick={() => setActiveTopicsTab('grandparent')}
                  style={{
                    padding: '12px 20px',
                    background: 'none',
                    border: 'none',
                    color: activeTopicsTab === 'grandparent' ? 'white' : 'rgba(255,255,255,0.5)',
                    fontSize: '15px',
                    fontWeight: activeTopicsTab === 'grandparent' ? '600' : '400',
                    cursor: 'pointer',
                    borderBottom: activeTopicsTab === 'grandparent' ? '2px solid #26a69a' : '2px solid transparent',
                    marginBottom: '-1px'
                  }}
                >
                  💝 Grandparent Topics
                </button>
              </div>
            )}
            
            {/* Modal Body */}
            <div style={{
              padding: '20px 24px',
              overflowY: 'auto',
              flex: 1
            }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                gap: '12px'
              }}>
                {(activeTopicsTab === 'parental' ? parentalTopics : grandparentTopics).map((topic, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      setShowSidebar(false);
                      setShowTopicsModal(false);
                      sendMessage(`I'd like help with: ${topic}`);
                    }}
                    style={{
                      padding: '14px 18px',
                      background: activeTopicsTab === 'parental' 
                        ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                        : 'linear-gradient(135deg, #26a69a 0%, #00897b 100%)',
                      border: 'none',
                      borderRadius: '6px',
                      color: '#264653',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'transform 0.2s, box-shadow 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = activeTopicsTab === 'parental'
                        ? '0 4px 12px rgba(102,126,234,0.4)'
                        : '0 4px 12px rgba(38,166,154,0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = 'none';
                    }}
                  >
                    {topic}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        @keyframes blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }
        html, body {
          background: ${fcColors.pageBg} !important;
          margin: 0;
          padding: 0;
          overflow: hidden;
        }
        div::-webkit-scrollbar {
          display: none;
        }
      `}</style>
      {/* Conversation History Modal */}
      {showConversationHistory && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            background: 'white', borderRadius: '16px', width: '90%', maxWidth: '500px',
            maxHeight: '80vh', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <div style={{
              padding: '20px', borderBottom: '1px solid #e5e7eb',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center'
            }}>
              <h2 style={{ margin: 0, fontSize: '20px', color: '#1f2937' }}>📜 Conversation History</h2>
              <button onClick={() => setShowConversationHistory(false)}
                style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#6b7280' }}>×</button>
            </div>
            <div style={{ padding: '16px', maxHeight: '50vh', overflowY: 'auto' }}>
              {loadingHistory ? (
                <p style={{ color: '#6b7280', textAlign: 'center' }}>Loading conversations...</p>
              ) : fcConversations.length === 0 ? (
                <p style={{ color: '#6b7280', textAlign: 'center' }}>No conversations yet</p>
              ) : (
                fcConversations.map((conv, idx) => (
                  <div key={conv.id || idx} onClick={() => loadConversationMessages(conv.id)}
                    style={{
                      padding: '16px', marginBottom: '12px', borderRadius: '12px',
                      background: conversationId === conv.id ? '#dbeafe' : '#f3f4f6',
                      cursor: 'pointer', border: conversationId === conv.id ? '2px solid #3b82f6' : '2px solid transparent',
                      transition: 'all 0.2s ease'
                    }}>
                    <div style={{ fontWeight: '600', color: '#1f2937', marginBottom: '4px' }}>
                      {conv.preview ? conv.preview.substring(0, 60) + '...' : 'Conversation'}
                    </div>
                    <div style={{ fontSize: '13px', color: '#6b7280' }}>
                      {conv.message_count || 0} messages
                      {conv.last_activity && ` • ${new Date(conv.last_activity).toLocaleDateString()}`}
                    </div>
                      <ConversationActions app="family" conversationId={conv.id} onDeleted={(id) => { setFcConversations(prev => prev.filter(c => c.id !== id)); if (conversationId === id) { setConversationId(null); setMessages([]); } }} />
                  </div>
                ))
              )}
            </div>
            <div style={{ padding: '16px', borderTop: '1px solid #e5e7eb', textAlign: 'center' }}>
              <button onClick={() => { setConversationId(null); setMessages([]); setShowConversationHistory(false); loadFamilyData(); }}
                style={{ padding: '12px 24px', background: '#10B981', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer' }}>
                + Start New Conversation
              </button>
            </div>
          </div>
        </div>
      )}

      <VoiceChoiceModal isOpen={showVoicePanel} theme={{ colors: { primary: '#264653', accent: '#2A9D8F' } }}
        voices={voiceList} choice={voiceChoice}
        onClose={() => { stopCoachVoice(); try { window.speechSynthesis.cancel(); } catch (e) { /* no speech */ } setShowVoicePanel(false); }}
        onChoose={(id) => { setVoiceChoice(id); saveCoachVoice(id); voice.setVoiceOn(true); }}
        onPreview={previewCoachVoice} voiceOn={voice.voiceEnabled} onToggleVoice={() => voice.setVoiceOn(!voice.voiceEnabled)} />
      <MPColorsModal isOpen={showColors} onClose={() => setShowColors(false)} schemes={Object.values(FC_SCHEMES)}
        active={fcScheme} onChoose={chooseScheme} accent="#2A9D8F" />
      {showPaywall && <PaywallModal onClose={() => setShowPaywall(false)} />}
    </div>
  );
};

export default FamilyCoachChat;
